/**
 * @fileoverview Live Stadium Combat Stage and HUD Component for "Farmeando Aura" (/screen).
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (60 FPS, 0% CPU in idle),
 * aggressive neon-skewed fighting game HUD bars, Bangers font numerals, respiratory timer,
 * non-repeating transformation deck cycles (1..50 slots) every 15 taps, comic combo popups
 * (4rem to 9rem with screen shake), turn-based dimming, KO damage flash, and ephemeral confetti.
 * 
 * @module components/Screen/Sections/FarmeandoAura/FarmeandoAuraGame
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme } from '../../../../utilities/BL/Fighters';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_GAME_CONFIGURATION = {
    gameBackgroundType: 'color',
    gameBackgroundColor: '#0f0f1e',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    player1Color: '#00ffcc',
    player2Color: '#ff0055',
    fontFamily: 'Bangers',
    player1Avatar: '',
    player1Winner: '',
    player2Avatar: '',
    player2Winner: ''
};

/**
 * Standard comic combo tiers specification.
 * @constant {Array<Object>}
 */
const DEFAULT_COMBO_TIERS = [
    { level: 1, threshold: 10, text: '🌟🌟 +10 DE AURA 🌟🌟', extraPoints: 15, fontSize: '4rem', shakeIntensity: 'light' },
    { level: 2, threshold: 20, text: '😎😎 +1,000 DE AURA 😎😎', extraPoints: 20, fontSize: '5rem', shakeIntensity: 'light' },
    { level: 3, threshold: 30, text: '🚀🚀 +10,000 DE AURA 🚀🚀', extraPoints: 25, fontSize: '6rem', shakeIntensity: 'medium' },
    { level: 4, threshold: 40, text: '🔥🔥 +100,000 DE AURA 🔥🔥', extraPoints: 30, fontSize: '7rem', shakeIntensity: 'medium' },
    { level: 5, threshold: 50, text: '⚡⚡ +1,000,000 DE AURA ⚡⚡', extraPoints: 40, fontSize: '8rem', shakeIntensity: 'heavy' },
    { level: 6, threshold: 100, text: '🌌 MODO DIOS 🌌', extraPoints: 50, fontSize: '9rem', shakeIntensity: 'god' }
];

/**
 * Confetti colors palette for victory explosion.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#00ffcc', '#ff0055', '#ffea00', '#00e5ff', '#ff3366', '#ffffff', '#76ff03'];

/**
 * Generates an array of Fisher-Yates shuffled indices without immediate repetitions on loop.
 * 
 * @function generateShuffledDeckIndices
 * @param {number} deckLength - Number of cards in the deck.
 * @param {number} [lastIndexToAvoid=-1] - Last card index to prevent immediate duplicate on loop.
 * @returns {Array<number>} Shuffled indices.
 */
const generateShuffledDeckIndices = (deckLength, lastIndexToAvoid = -1) => {
    if (deckLength <= 0) return [0];
    if (deckLength === 1) return [0];

    const indicesArray = Array.from({ length: deckLength }, (_, index) => index);

    for (let currentIndex = indicesArray.length - 1; currentIndex > 0; currentIndex--) {
        const randomizedIndex = Math.floor(Math.random() * (currentIndex + 1));
        const temporaryValue = indicesArray[currentIndex];
        indicesArray[currentIndex] = indicesArray[randomizedIndex];
        indicesArray[randomizedIndex] = temporaryValue;
    }

    // Ensure the first element of the new deck is not identical to the last shown item
    if (lastIndexToAvoid >= 0 && indicesArray[0] === lastIndexToAvoid && deckLength > 1) {
        const swapPosition = Math.floor(Math.random() * (deckLength - 1)) + 1;
        const temp = indicesArray[0];
        indicesArray[0] = indicesArray[swapPosition];
        indicesArray[swapPosition] = temp;
    }

    return indicesArray;
};

/**
 * Safely parses any JSON string, array, or null into a validated array.
 * 
 * @function safeParseArray
 * @param {Array|string|null} rawValue - Raw input value.
 * @returns {Array} Parsed array or empty array fallback.
 */
const safeParseArray = (rawValue) => {
    if (!rawValue) return [];
    if (Array.isArray(rawValue)) return rawValue;
    if (typeof rawValue === 'string') {
        try {
            const parsed = JSON.parse(rawValue);
            return Array.isArray(parsed) ? parsed : [];
        } catch (parseError) {
            return [];
        }
    }
    return [];
};

/**
 * FightersGame / FarmeandoAuraGame Component (ADSL Replica)
 * 
 * @function FightersGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - Incoming WebSocket packet or snapshot from parent Screen router.
 * @param {Object} [props.currentMatchState] - Match state passed directly by Screen.js.
 * @param {Object} [props.theme] - Theme object directly passed from Screen.js.
 * @param {Object} [props.config] - Config object directly passed from Screen.js.
 * @param {string} [props.roomId] - Room GUID.
 * @returns {React.ReactElement} The rendered stadium game HUD and stage.
 */
export const FightersGame = ({
    payload,
    currentMatchState,
    theme: propTheme,
    config: propConfig,
    roomId
}) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA Y CONFIGURACIÓN
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 2. ESTADO DEL MOTOR DE JUEGO (TELEMETRÍA EN TIEMPO REAL)
    // =========================================================================
    const [combatEngineState, setCombatEngineState] = useState({
        state: 'idle', // idle, game_ready, countdown, playing, paused, finished
        roundDuration: 10,
        gameMode: 'score', // score | health_ko | health_timed | simultaneous
        startedAt: null,
        endsAt: null,
        remainingSeconds: 10,
        scoreJ1: 0,
        scoreJ2: 0,
        totalTapsJ1: 0,
        totalTapsJ2: 0,
        activeFansJ1: 0,
        activeFansJ2: 0,
        hpJ1: 100.0,
        hpJ2: 100.0,
        comboJ1: 0,
        comboJ2: 0,
        transformIndexJ1: 0,
        transformIndexJ2: 0,
        showComboPopups: true,
        showDeckTransforms: true,
        deckTransformInterval: 15,
        winner: null
    });

    // =========================================================================
    // 3. ESTADOS VISUALES DE ANIMACIÓN (ZERO-CANVAS GPU)
    // =========================================================================
    const [countdownDisplayNumber, setCountdownDisplayNumber] = useState(null);
    const [screenShakeClassName, setScreenShakeClassName] = useState('');
    const [activeFloatingCombosList, setActiveFloatingCombosList] = useState([]);
    const [ephemeralConfettiParticleList, setEphemeralConfettiParticleList] = useState([]);
    const [isCinematicDamageFlashActive, setIsCinematicDamageFlashActive] = useState(false);

    // Sprites y Transformaciones de Luchadores J1 y J2
    const [playerOneCurrentSpriteUrl, setPlayerOneCurrentSpriteUrl] = useState('');
    const [playerTwoCurrentSpriteUrl, setPlayerTwoCurrentSpriteUrl] = useState('');
    const [playerOneIsSlidingTransformation, setPlayerOneIsSlidingTransformation] = useState(false);
    const [playerTwoIsSlidingTransformation, setPlayerTwoIsSlidingTransformation] = useState(false);

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA GESTIÓN DE MAZOS Y TIMERS
    // =========================================================================
    const playerOneDeckIndicesPoolRef = useRef([]);
    const playerOneDeckPointerRef = useRef(0);
    const playerTwoDeckIndicesPoolRef = useRef([]);
    const playerTwoDeckPointerRef = useRef(0);

    const previousComboLevelJ1Ref = useRef(0);
    const previousComboLevelJ2Ref = useRef(0);
    const previousTransformIndexJ1Ref = useRef(0);
    const previousTransformIndexJ2Ref = useRef(0);

    const lastComboEventKeyRef = useRef(null);
    const lastDeckTransformKeyRef = useRef(null);
    const hasFinishedCinematicTriggeredRef = useRef(false);
    const lastInitializedThemeSignatureRef = useRef('');
    const lastThemeKeyRef = useRef('');

    const localCountdownIntervalTimerRef = useRef(null);
    const screenShakeResetTimerRef = useRef(null);
    const confettiAutoDestroyTimerRef = useRef(null);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    // =========================================================================
    // 5. INICIALIZACIÓN DE SPRITES Y BARAJAS DE TRANSFORMACIÓN
    // =========================================================================
    const initializeTransformationDecks = useCallback((themeObject) => {
        if (!themeObject) return;
        const rawDeck = safeParseArray(themeObject?.deckImages);
        const themeSig = `${themeObject.id || ''}_${themeObject.name || ''}_${themeObject.player1Avatar || ''}_${themeObject.player2Avatar || ''}_${rawDeck.length}`;
        if (lastInitializedThemeSignatureRef.current === themeSig) return;
        lastInitializedThemeSignatureRef.current = themeSig;

        const deckLength = rawDeck.length > 0 ? rawDeck.length : 27;

        playerOneDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckLength);
        playerOneDeckPointerRef.current = 0;

        playerTwoDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckLength);
        playerTwoDeckPointerRef.current = 0;

        // Set initial avatars
        const initialAvatarJ1 = themeObject?.player1Avatar
            ? resolveMediaUrl(themeObject.player1Avatar)
            : (rawDeck[0] ? resolveMediaUrl(rawDeck[0]) : '');

        const initialAvatarJ2 = themeObject?.player2Avatar
            ? resolveMediaUrl(themeObject.player2Avatar)
            : (rawDeck[0] ? resolveMediaUrl(rawDeck[0]) : '');

        setPlayerOneCurrentSpriteUrl(initialAvatarJ1);
        setPlayerTwoCurrentSpriteUrl(initialAvatarJ2);

        previousTransformIndexJ1Ref.current = 0;
        previousTransformIndexJ2Ref.current = 0;
        previousComboLevelJ1Ref.current = 0;
        previousComboLevelJ2Ref.current = 0;
    }, [resolveMediaUrl]);

    // =========================================================================
    // 6. DISPARADOR DE TRANSFORMACIÓN SIN REPETICIÓN (CADA 15 TAPS PROMEDIO)
    // =========================================================================
    const triggerPlayerTransformation = useCallback((playerSide, themeObject, explicitImageUrl) => {
        if (combatEngineState.showDeckTransforms === false) return;
        const rawDeck = safeParseArray(themeObject?.deckImages);
        const deckCount = rawDeck.length > 0 ? rawDeck.length : 1;
        const normalizedSide = (playerSide === 'player2' || playerSide === 'J2') ? 'J2' : 'J1';

        if (normalizedSide === 'J1') {
            let nextSpriteUrl = explicitImageUrl;
            if (!nextSpriteUrl) {
                let nextPointer = playerOneDeckPointerRef.current + 1;
                if (nextPointer >= playerOneDeckIndicesPoolRef.current.length) {
                    const lastCardIndex = playerOneDeckIndicesPoolRef.current[playerOneDeckIndicesPoolRef.current.length - 1];
                    playerOneDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckCount, lastCardIndex);
                    nextPointer = 0;
                }
                playerOneDeckPointerRef.current = nextPointer;

                const nextCardSlotIndex = playerOneDeckIndicesPoolRef.current[nextPointer];
                nextSpriteUrl = rawDeck[nextCardSlotIndex]
                    ? resolveMediaUrl(rawDeck[nextCardSlotIndex])
                    : (themeObject?.player1Avatar ? resolveMediaUrl(themeObject.player1Avatar) : '');
            } else {
                nextSpriteUrl = resolveMediaUrl(nextSpriteUrl);
            }

            if (!nextSpriteUrl) return;

            // GPU Accelerated Slide-Out -> Slide-In
            setPlayerOneIsSlidingTransformation(true);
            setTimeout(() => {
                setPlayerOneCurrentSpriteUrl(nextSpriteUrl);
                setTimeout(() => {
                    setPlayerOneIsSlidingTransformation(false);
                }, 180);
            }, 140);
        } else {
            let nextSpriteUrl = explicitImageUrl;
            if (!nextSpriteUrl) {
                let nextPointer = playerTwoDeckPointerRef.current + 1;
                if (nextPointer >= playerTwoDeckIndicesPoolRef.current.length) {
                    const lastCardIndex = playerTwoDeckIndicesPoolRef.current[playerTwoDeckIndicesPoolRef.current.length - 1];
                    playerTwoDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckCount, lastCardIndex);
                    nextPointer = 0;
                }
                playerTwoDeckPointerRef.current = nextPointer;

                const nextCardSlotIndex = playerTwoDeckIndicesPoolRef.current[nextPointer];
                nextSpriteUrl = rawDeck[nextCardSlotIndex]
                    ? resolveMediaUrl(rawDeck[nextCardSlotIndex])
                    : (themeObject?.player2Avatar ? resolveMediaUrl(themeObject.player2Avatar) : '');
            } else {
                nextSpriteUrl = resolveMediaUrl(nextSpriteUrl);
            }

            if (!nextSpriteUrl) return;

            // GPU Accelerated Slide-Out -> Slide-In
            setPlayerTwoIsSlidingTransformation(true);
            setTimeout(() => {
                setPlayerTwoCurrentSpriteUrl(nextSpriteUrl);
                setTimeout(() => {
                    setPlayerTwoIsSlidingTransformation(false);
                }, 180);
            }, 140);
        }
    }, [resolveMediaUrl, combatEngineState.showDeckTransforms]);

    // =========================================================================
    // 7. DISPARADOR DE COMBOS POPUP Y SCREEN-SHAKE GPU
    // =========================================================================
    const triggerComicComboPopup = useCallback((playerSide, comboLevel, themeObject, explicitText, explicitFontSize) => {
        if (comboLevel <= 0 && !explicitText) return;
        if (combatEngineState.showComboPopups === false) return;

        const normalizedSide = (playerSide === 'player2' || playerSide === 'J2') ? 'J2' : 'J1';

        // Obtain combo configuration
        const customCombosList = safeParseArray(themeObject?.comboTexts);
        const matchedCustomCombo = customCombosList.find((item) => item.level === comboLevel);
        const defaultTier = DEFAULT_COMBO_TIERS.find((item) => item.level === comboLevel) || DEFAULT_COMBO_TIERS[DEFAULT_COMBO_TIERS.length - 1];

        const comboText = explicitText || matchedCustomCombo?.text || defaultTier.text;
        const comboFontSize = explicitFontSize || matchedCustomCombo?.fontSize || defaultTier.fontSize;
        const shakeIntensity = defaultTier.shakeIntensity || 'medium';

        const uniqueComboIdentifier = `${normalizedSide}-${comboLevel}-${Date.now()}-${Math.random()}`;
        const newComboItem = {
            id: uniqueComboIdentifier,
            side: normalizedSide,
            text: comboText,
            fontSize: comboFontSize,
            level: comboLevel
        };

        setActiveFloatingCombosList((previousCombos) => [...previousCombos, newComboItem]);

        // GPU Screen Shake Application
        if (screenShakeResetTimerRef.current) {
            clearTimeout(screenShakeResetTimerRef.current);
        }
        setScreenShakeClassName(`aura-screen-game--shake-${shakeIntensity}`);
        screenShakeResetTimerRef.current = setTimeout(() => {
            setScreenShakeClassName('');
        }, 550);

        // Auto remove popup after 1.8s
        setTimeout(() => {
            setActiveFloatingCombosList((previousCombos) =>
                previousCombos.filter((item) => item.id !== uniqueComboIdentifier)
            );
        }, 1800);
    }, []);

    // =========================================================================
    // 8. DISPARADOR DE CONFETI EFÍMERO (3 SEGUNDOS EXACTOS - ZERO LEAKS)
    // =========================================================================
    const spawnEphemeralConfettiBurst = useCallback(() => {
        const TOTAL_CONFETTI_PARTICLES_COUNT = 70;
        const generatedParticles = Array.from({ length: TOTAL_CONFETTI_PARTICLES_COUNT }, (_, particleIndex) => {
            const randomizedColor = CONFETTI_COLOR_PALETTE[Math.floor(Math.random() * CONFETTI_COLOR_PALETTE.length)];
            const randomizedHorizontalPosition = Math.random() * 100; // 0% to 100% viewport width
            const randomizedFallDuration = 1.6 + Math.random() * 1.2; // 1.6s to 2.8s
            const randomizedDelay = Math.random() * 0.4; // 0s to 0.4s
            const randomizedSize = 12 + Math.random() * 16; // 12px to 28px
            const isCircular = Math.random() > 0.5;

            return {
                id: `confetti-${particleIndex}-${Date.now()}`,
                color: randomizedColor,
                leftPercentage: randomizedHorizontalPosition,
                durationSeconds: randomizedFallDuration,
                delaySeconds: randomizedDelay,
                sizePixels: randomizedSize,
                borderRadius: isCircular ? '50%' : '2px'
            };
        });

        setEphemeralConfettiParticleList(generatedParticles);

        if (confettiAutoDestroyTimerRef.current) {
            clearTimeout(confettiAutoDestroyTimerRef.current);
        }

        // Strict 3 seconds auto-destruction guarantee (Zero Memory Leaks)
        confettiAutoDestroyTimerRef.current = setTimeout(() => {
            setEphemeralConfettiParticleList([]);
        }, 3000);
    }, []);

    // =========================================================================
    // 9. CINEMÁTICA DE K.O. Y PODIUM
    // =========================================================================
    const triggerKnockoutCinematic = useCallback((winnerResult, themeObject) => {
        setIsCinematicDamageFlashActive(true);
        setTimeout(() => {
            setIsCinematicDamageFlashActive(false);
        }, 650);

        const normalizedWinner = (winnerResult === 'J1' || winnerResult === 'player1') ? 'J1'
            : ((winnerResult === 'J2' || winnerResult === 'player2') ? 'J2' : 'Empate');

        if (normalizedWinner === 'J1') {
            const winnerAvatarUrl = themeObject?.player1Winner
                ? resolveMediaUrl(themeObject.player1Winner)
                : playerOneCurrentSpriteUrl;
            if (winnerAvatarUrl) setPlayerOneCurrentSpriteUrl(winnerAvatarUrl);
            spawnEphemeralConfettiBurst();
        } else if (normalizedWinner === 'J2') {
            const winnerAvatarUrl = themeObject?.player2Winner
                ? resolveMediaUrl(themeObject.player2Winner)
                : playerTwoCurrentSpriteUrl;
            if (winnerAvatarUrl) setPlayerTwoCurrentSpriteUrl(winnerAvatarUrl);
            spawnEphemeralConfettiBurst();
        } else {
            // Empate
            spawnEphemeralConfettiBurst();
        }
    }, [resolveMediaUrl, playerOneCurrentSpriteUrl, playerTwoCurrentSpriteUrl, spawnEphemeralConfettiBurst]);

    // =========================================================================
    // 10. CARGA INICIAL DEL TEMA Y ESTADO DE SALA
    // =========================================================================
    const fetchInitialGameConfiguration = useCallback(async () => {
        try {
            const configurationResponse = await GetConfig(roomId);
            if (configurationResponse.success && configurationResponse.data) {
                const roomConfig = configurationResponse.data;
                if (roomConfig.activeTheme) {
                    setActiveThemeData(roomConfig.activeTheme);
                } else if (roomConfig.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfig.activeThemeId, roomId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }

                setCombatEngineState((previousState) => ({
                    ...previousState,
                    roundDuration: roomConfig.roundDuration || previousState.roundDuration,
                    gameMode: roomConfig.gameMode || previousState.gameMode,
                    showComboPopups: roomConfig.showComboPopups ?? previousState.showComboPopups,
                    showDeckTransforms: roomConfig.showDeckTransforms ?? previousState.showDeckTransforms,
                    deckTransformInterval: roomConfig.deckTransformInterval ?? previousState.deckTransformInterval,
                    state: roomConfig.currentState || previousState.state
                }));
            }
        } catch (initialFetchError) {
            // Non-blocking fallback to default configuration
        }
    }, [roomId]);

    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData(directTheme);
        } else if (propConfig?.theme) {
            setActiveThemeData(propConfig.theme);
        } else if (!activeThemeData) {
            fetchInitialGameConfiguration();
        }
    }, [propTheme, propConfig, activeThemeData, fetchInitialGameConfiguration]);

    useEffect(() => {
        if (activeThemeData) {
            initializeTransformationDecks(activeThemeData);
        }
    }, [activeThemeData, initializeTransformationDecks]);

    // =========================================================================
    // 11. SINCRONIZACIÓN REACTIVA CON CURRENTMATCHSTATE (DESDE SCREEN.JS)
    // =========================================================================
    useEffect(() => {
        if (!currentMatchState || typeof currentMatchState !== 'object') return;

        const rawPhase = currentMatchState.phase || currentMatchState.state;
        const normalizedPhase = rawPhase ? String(rawPhase).toLowerCase() : null;

        const score1 = currentMatchState.scoreJ1 ?? currentMatchState.ScoreJ1 ?? currentMatchState.player1?.score ?? currentMatchState.Player1?.Score ?? combatEngineState.scoreJ1;
        const score2 = currentMatchState.scoreJ2 ?? currentMatchState.ScoreJ2 ?? currentMatchState.player2?.score ?? currentMatchState.Player2?.Score ?? combatEngineState.scoreJ2;
        const hp1 = currentMatchState.hpJ1 ?? currentMatchState.HpJ1 ?? currentMatchState.player1?.hp ?? currentMatchState.Player1?.Hp ?? combatEngineState.hpJ1 ?? 100.0;
        const hp2 = currentMatchState.hpJ2 ?? currentMatchState.HpJ2 ?? currentMatchState.player2?.hp ?? currentMatchState.Player2?.Hp ?? combatEngineState.hpJ2 ?? 100.0;
        const taps1 = currentMatchState.totalTapsJ1 ?? currentMatchState.TotalTapsJ1 ?? currentMatchState.player1?.totalTaps ?? currentMatchState.Player1?.TotalTaps ?? combatEngineState.totalTapsJ1;
        const taps2 = currentMatchState.totalTapsJ2 ?? currentMatchState.TotalTapsJ2 ?? currentMatchState.player2?.totalTaps ?? currentMatchState.Player2?.TotalTaps ?? combatEngineState.totalTapsJ2;
        const fans1 = currentMatchState.activeFansJ1 ?? currentMatchState.ActiveFansJ1 ?? currentMatchState.player1?.activeFans ?? currentMatchState.Player1?.ActiveFans ?? combatEngineState.activeFansJ1;
        const fans2 = currentMatchState.activeFansJ2 ?? currentMatchState.ActiveFansJ2 ?? currentMatchState.player2?.activeFans ?? currentMatchState.Player2?.ActiveFans ?? combatEngineState.activeFansJ2;

        const combo1 = currentMatchState.comboJ1 ?? currentMatchState.ComboJ1 ?? currentMatchState.player1?.currentComboLevel ?? currentMatchState.Player1?.CurrentComboLevel ?? 0;
        const combo2 = currentMatchState.comboJ2 ?? currentMatchState.ComboJ2 ?? currentMatchState.player2?.currentComboLevel ?? currentMatchState.Player2?.CurrentComboLevel ?? 0;

        const trans1 = currentMatchState.transformIndexJ1 ?? currentMatchState.TransformIndexJ1 ?? currentMatchState.player1?.currentDeckIndex ?? currentMatchState.Player1?.CurrentDeckIndex ?? 0;
        const trans2 = currentMatchState.transformIndexJ2 ?? currentMatchState.TransformIndexJ2 ?? currentMatchState.player2?.currentDeckIndex ?? currentMatchState.Player2?.CurrentDeckIndex ?? 0;

        const incomingGameMode = currentMatchState.gameMode || currentMatchState.GameMode || combatEngineState.gameMode;
        const incomingShowComboPopups = currentMatchState.showComboPopups ?? currentMatchState.ShowComboPopups ?? combatEngineState.showComboPopups;
        const incomingShowDeckTransforms = currentMatchState.showDeckTransforms ?? currentMatchState.ShowDeckTransforms ?? combatEngineState.showDeckTransforms;
        const incomingDeckTransformInterval = currentMatchState.deckTransformInterval ?? currentMatchState.DeckTransformInterval ?? combatEngineState.deckTransformInterval;

        const isHealthMode = incomingGameMode === 'health_ko' ||
            incomingGameMode === 'health_timed' ||
            incomingGameMode === 'HealthKO' ||
            incomingGameMode === 'HealthTimed';

        // Determinar ganador normalizado
        let rawWinner = currentMatchState.winner !== undefined && currentMatchState.winner !== null
            ? currentMatchState.winner
            : (currentMatchState.leadingPlayer || null);

        let normalizedWinner = null;
        if (rawWinner === 'player1' || rawWinner === 'J1') {
            normalizedWinner = 'J1';
        } else if (rawWinner === 'player2' || rawWinner === 'J2') {
            normalizedWinner = 'J2';
        } else if (rawWinner === 'tie' || rawWinner === 'Empate') {
            normalizedWinner = 'Empate';
        } else if (normalizedPhase === 'finished') {
            if (isHealthMode) {
                if (hp1 > hp2) normalizedWinner = 'J1';
                else if (hp2 > hp1) normalizedWinner = 'J2';
                else normalizedWinner = 'Empate';
            } else {
                if (score1 > score2) normalizedWinner = 'J1';
                else if (score2 > score1) normalizedWinner = 'J2';
                else normalizedWinner = 'Empate';
            }
        }

        // Sincronización de tema si viene en currentMatchState
        if (currentMatchState.theme && typeof currentMatchState.theme === 'object') {
            const incomingTheme = currentMatchState.theme;
            const themeKey = `${incomingTheme.id || ''}_${incomingTheme.name || ''}_${incomingTheme.player1Avatar || ''}_${incomingTheme.player2Avatar || ''}_${(incomingTheme.deckImages || []).length}`;
            if (themeKey !== lastThemeKeyRef.current) {
                lastThemeKeyRef.current = themeKey;
                setActiveThemeData(incomingTheme);
            }
        }

        // 1. Manejo de fases
        if (normalizedPhase === 'countdown') {
            const cd = currentMatchState.countdown ?? currentMatchState.count ?? currentMatchState.secondsRemaining ?? 3;
            setCountdownDisplayNumber(cd);
            hasFinishedCinematicTriggeredRef.current = false;
            previousComboLevelJ1Ref.current = 0;
            previousComboLevelJ2Ref.current = 0;
            previousTransformIndexJ1Ref.current = 0;
            previousTransformIndexJ2Ref.current = 0;
        } else if (normalizedPhase === 'playing') {
            setCountdownDisplayNumber(null);
            hasFinishedCinematicTriggeredRef.current = false;
        } else if (normalizedPhase === 'game_ready' || normalizedPhase === 'idle') {
            setCountdownDisplayNumber(null);
            hasFinishedCinematicTriggeredRef.current = false;
            previousComboLevelJ1Ref.current = 0;
            previousComboLevelJ2Ref.current = 0;
            previousTransformIndexJ1Ref.current = 0;
            previousTransformIndexJ2Ref.current = 0;
        } else if (normalizedPhase === 'finished') {
            setCountdownDisplayNumber(null);
            if (!hasFinishedCinematicTriggeredRef.current) {
                hasFinishedCinematicTriggeredRef.current = true;
                triggerKnockoutCinematic(normalizedWinner, activeThemeData || currentMatchState.theme);
            }
        }

        // 2. Detección reactiva de Combos
        const targetTheme = activeThemeData || currentMatchState.theme;
        if (normalizedPhase === 'playing' || combatEngineState.state === 'playing') {
            if (combo1 > previousComboLevelJ1Ref.current) {
                previousComboLevelJ1Ref.current = combo1;
                triggerComicComboPopup('J1', combo1, targetTheme);
            }
            if (combo2 > previousComboLevelJ2Ref.current) {
                previousComboLevelJ2Ref.current = combo2;
                triggerComicComboPopup('J2', combo2, targetTheme);
            }

            if (currentMatchState.lastComboEvent) {
                const ev = currentMatchState.lastComboEvent;
                const key = `${ev.playerChoice || ev.side}_${ev.comboLevel}_${ev.comboText || ''}_${ev.threshold || ''}`;
                if (key && lastComboEventKeyRef.current !== key) {
                    lastComboEventKeyRef.current = key;
                    const side = (ev.playerChoice === 'player2' || ev.side === 'J2') ? 'J2' : 'J1';
                    triggerComicComboPopup(side, ev.comboLevel || 1, targetTheme, ev.comboText);
                }
            }

            // 3. Detección reactiva de Transformaciones de imagen
            if (trans1 > previousTransformIndexJ1Ref.current) {
                previousTransformIndexJ1Ref.current = trans1;
                triggerPlayerTransformation('J1', targetTheme);
            }
            if (trans2 > previousTransformIndexJ2Ref.current) {
                previousTransformIndexJ2Ref.current = trans2;
                triggerPlayerTransformation('J2', targetTheme);
            }

            if (currentMatchState.lastDeckTransform) {
                const dt = currentMatchState.lastDeckTransform;
                const key = `${dt.playerChoice || dt.side}_${dt.deckSlotIndex ?? dt.deckIndex ?? ''}_${dt.deckImageUrl || ''}`;
                if (key && lastDeckTransformKeyRef.current !== key) {
                    lastDeckTransformKeyRef.current = key;
                    const side = (dt.playerChoice === 'player2' || dt.side === 'J2') ? 'J2' : 'J1';
                    triggerPlayerTransformation(side, targetTheme, dt.deckImageUrl);
                }
            }
        }

        // 4. Actualización del estado del motor
        setCombatEngineState((prev) => ({
            ...prev,
            state: normalizedPhase === 'playing' ? 'playing'
                : normalizedPhase === 'countdown' ? 'countdown'
                : normalizedPhase === 'finished' ? 'finished'
                : normalizedPhase === 'paused' ? 'paused'
                : normalizedPhase === 'game_ready' ? 'game_ready'
                : prev.state,
            gameMode: incomingGameMode,
            showComboPopups: incomingShowComboPopups,
            showDeckTransforms: incomingShowDeckTransforms,
            deckTransformInterval: incomingDeckTransformInterval,
            scoreJ1: score1,
            scoreJ2: score2,
            hpJ1: hp1,
            hpJ2: hp2,
            totalTapsJ1: taps1,
            totalTapsJ2: taps2,
            activeFansJ1: fans1,
            activeFansJ2: fans2,
            comboJ1: combo1,
            comboJ2: combo2,
            transformIndexJ1: trans1,
            transformIndexJ2: trans2,
            winner: normalizedWinner || prev.winner,
            remainingSeconds: currentMatchState.secondsRemaining ?? currentMatchState.remainingSeconds ?? prev.remainingSeconds
        }));
    }, [
        currentMatchState,
        activeThemeData,
        combatEngineState.state,
        combatEngineState.scoreJ1,
        combatEngineState.scoreJ2,
        combatEngineState.hpJ1,
        combatEngineState.hpJ2,
        combatEngineState.gameMode,
        combatEngineState.showComboPopups,
        combatEngineState.showDeckTransforms,
        triggerComicComboPopup,
        triggerPlayerTransformation,
        triggerKnockoutCinematic
    ]);

    // =========================================================================
    // 12. PROCESAMIENTO DE MENSAJES WEBSOCKET STANDALONE (SI SE PASA PAYLOAD DIRECTO)
    // =========================================================================
    useEffect(() => {
        if (!payload || !payload.type) return;

        const normalizedMessageType = payload.type.toLowerCase();
        const incomingData = payload.data || payload.payload || {};

        switch (normalizedMessageType) {
            case 'aura.prepare':
            case 'aura_show_game':
            case 'fighters_game_scene_shown':
            case 'fighters_game_ready': {
                const snapshotState = payload.state || incomingData;
                if (snapshotState.currentTheme) {
                    setActiveThemeData(snapshotState.currentTheme);
                }
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'game_ready',
                    scoreJ1: 0,
                    scoreJ2: 0,
                    hpJ1: 100.0,
                    hpJ2: 100.0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0,
                    comboJ1: 0,
                    comboJ2: 0,
                    transformIndexJ1: 0,
                    transformIndexJ2: 0,
                    winner: null,
                    gameMode: snapshotState.gameMode || snapshotState.GameMode || previous.gameMode,
                    showComboPopups: snapshotState.showComboPopups ?? snapshotState.ShowComboPopups ?? previous.showComboPopups,
                    showDeckTransforms: snapshotState.showDeckTransforms ?? snapshotState.ShowDeckTransforms ?? previous.showDeckTransforms,
                    deckTransformInterval: snapshotState.deckTransformInterval ?? snapshotState.DeckTransformInterval ?? previous.deckTransformInterval,
                    remainingSeconds: snapshotState.roundDuration || previous.roundDuration || 10
                }));
                setCountdownDisplayNumber(null);
                setEphemeralConfettiParticleList([]);
                hasFinishedCinematicTriggeredRef.current = false;
                break;
            }

            case 'aura_countdown':
            case 'fighters_countdown':
            case 'fighters_countdown_started': {
                const countdownValue = payload.countdown ?? payload.count ?? payload.secondsRemaining ?? incomingData.countdown ?? incomingData.count ?? incomingData.secondsRemaining ?? 3;
                setCountdownDisplayNumber(countdownValue);
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'countdown',
                    winner: null
                }));
                hasFinishedCinematicTriggeredRef.current = false;
                break;
            }

            case 'aura_round_started':
            case 'fighters_round_started': {
                setCountdownDisplayNumber(null);
                hasFinishedCinematicTriggeredRef.current = false;
                const startedAtMs = payload.startedAt || incomingData.startedAt;
                const endsAtMs = payload.endsAt || incomingData.endsAt;
                const roundSec = payload.durationSeconds || incomingData.durationSeconds || 10;
                const gameMode = payload.gameMode || incomingData.gameMode || payload.GameMode || incomingData.GameMode || 'score';
                const showCombo = payload.showComboPopups ?? incomingData.showComboPopups ?? payload.ShowComboPopups ?? incomingData.ShowComboPopups ?? true;
                const showDeck = payload.showDeckTransforms ?? incomingData.showDeckTransforms ?? payload.ShowDeckTransforms ?? incomingData.ShowDeckTransforms ?? true;
                const deckInterval = payload.deckTransformInterval ?? incomingData.deckTransformInterval ?? payload.DeckTransformInterval ?? incomingData.DeckTransformInterval ?? 15;

                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'playing',
                    startedAt: startedAtMs,
                    endsAt: endsAtMs,
                    roundDuration: roundSec,
                    gameMode: gameMode,
                    showComboPopups: showCombo,
                    showDeckTransforms: showDeck,
                    deckTransformInterval: deckInterval,
                    winner: null,
                    scoreJ1: 0,
                    scoreJ2: 0,
                    hpJ1: 100.0,
                    hpJ2: 100.0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0
                }));

                if (activeThemeData) {
                    initializeTransformationDecks(activeThemeData);
                }
                break;
            }

            case 'aura_telemetry':
            case 'fighters_telemetry': {
                const telemetryData = payload.data || payload.payload || payload;
                const newScoreJ1 = telemetryData.scoreJ1 ?? telemetryData.ScoreJ1 ?? telemetryData.player1?.score ?? telemetryData.Player1?.Score ?? combatEngineState.scoreJ1;
                const newScoreJ2 = telemetryData.scoreJ2 ?? telemetryData.ScoreJ2 ?? telemetryData.player2?.score ?? telemetryData.Player2?.Score ?? combatEngineState.scoreJ2;
                const newHpJ1 = telemetryData.hpJ1 ?? telemetryData.HpJ1 ?? telemetryData.player1?.hp ?? telemetryData.Player1?.Hp ?? combatEngineState.hpJ1;
                const newHpJ2 = telemetryData.hpJ2 ?? telemetryData.HpJ2 ?? telemetryData.player2?.hp ?? telemetryData.Player2?.Hp ?? combatEngineState.hpJ2;
                const newTapsJ1 = telemetryData.totalTapsJ1 ?? telemetryData.TotalTapsJ1 ?? telemetryData.player1?.totalTaps ?? telemetryData.Player1?.TotalTaps ?? combatEngineState.totalTapsJ1;
                const newTapsJ2 = telemetryData.totalTapsJ2 ?? telemetryData.TotalTapsJ2 ?? telemetryData.player2?.totalTaps ?? telemetryData.Player2?.TotalTaps ?? combatEngineState.totalTapsJ2;
                const newFansJ1 = telemetryData.activeFansJ1 ?? telemetryData.ActiveFansJ1 ?? telemetryData.player1?.activeFans ?? telemetryData.Player1?.ActiveFans ?? combatEngineState.activeFansJ1;
                const newFansJ2 = telemetryData.activeFansJ2 ?? telemetryData.ActiveFansJ2 ?? telemetryData.player2?.activeFans ?? telemetryData.Player2?.ActiveFans ?? combatEngineState.activeFansJ2;
                const newComboJ1 = telemetryData.comboJ1 ?? telemetryData.ComboJ1 ?? telemetryData.player1?.currentComboLevel ?? telemetryData.Player1?.CurrentComboLevel ?? 0;
                const newComboJ2 = telemetryData.comboJ2 ?? telemetryData.ComboJ2 ?? telemetryData.player2?.currentComboLevel ?? telemetryData.Player2?.CurrentComboLevel ?? 0;
                const newTransformJ1 = telemetryData.transformIndexJ1 ?? telemetryData.TransformIndexJ1 ?? telemetryData.player1?.currentDeckIndex ?? telemetryData.Player1?.CurrentDeckIndex ?? 0;
                const newTransformJ2 = telemetryData.transformIndexJ2 ?? telemetryData.TransformIndexJ2 ?? telemetryData.player2?.currentDeckIndex ?? telemetryData.Player2?.CurrentDeckIndex ?? 0;

                // Check for new combos
                if (newComboJ1 > previousComboLevelJ1Ref.current) {
                    triggerComicComboPopup('J1', newComboJ1, activeThemeData);
                    previousComboLevelJ1Ref.current = newComboJ1;
                }
                if (newComboJ2 > previousComboLevelJ2Ref.current) {
                    triggerComicComboPopup('J2', newComboJ2, activeThemeData);
                    previousComboLevelJ2Ref.current = newComboJ2;
                }

                // Check for new transformations
                if (newTransformJ1 > previousTransformIndexJ1Ref.current) {
                    triggerPlayerTransformation('J1', activeThemeData);
                    previousTransformIndexJ1Ref.current = newTransformJ1;
                }
                if (newTransformJ2 > previousTransformIndexJ2Ref.current) {
                    triggerPlayerTransformation('J2', activeThemeData);
                    previousTransformIndexJ2Ref.current = newTransformJ2;
                }

                setCombatEngineState((previous) => ({
                    ...previous,
                    scoreJ1: newScoreJ1,
                    scoreJ2: newScoreJ2,
                    hpJ1: newHpJ1,
                    hpJ2: newHpJ2,
                    totalTapsJ1: newTapsJ1,
                    totalTapsJ2: newTapsJ2,
                    activeFansJ1: newFansJ1,
                    activeFansJ2: newFansJ2,
                    comboJ1: newComboJ1,
                    comboJ2: newComboJ2,
                    transformIndexJ1: newTransformJ1,
                    transformIndexJ2: newTransformJ2,
                    remainingSeconds: telemetryData.secondsRemaining ?? telemetryData.SecondsRemaining ?? (typeof telemetryData.remainingMs === 'number'
                        ? Math.max(0, Math.round((telemetryData.remainingMs / 1000) * 10) / 10)
                        : previous.remainingSeconds)
                }));
                break;
            }

            case 'aura_combo':
            case 'fighters_combo_triggered': {
                const comboSide = payload.side || incomingData.playerChoice || incomingData.side || 'J1';
                const comboLevel = payload.comboLevel || incomingData.comboLevel || 1;
                const comboTxt = payload.comboText || incomingData.comboText;
                triggerComicComboPopup(comboSide, comboLevel, activeThemeData, comboTxt);
                break;
            }

            case 'aura_transform':
            case 'fighters_deck_transform': {
                const transformSide = payload.side || incomingData.playerChoice || incomingData.side || 'J1';
                const imgUrl = payload.deckImageUrl || incomingData.deckImageUrl;
                triggerPlayerTransformation(transformSide, activeThemeData, imgUrl);
                break;
            }

            case 'aura_paused':
            case 'fighters_paused': {
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'paused',
                    remainingSeconds: payload.remainingSeconds ?? previous.remainingSeconds
                }));
                break;
            }

            case 'aura_resumed':
            case 'fighters_resumed': {
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'playing',
                    endsAt: payload.endsAt ?? previous.endsAt
                }));
                break;
            }

            case 'aura_game_over':
            case 'fighters_game_over': {
                const rawWinner = payload.winner || incomingData.winner || 'Empate';
                const finalWinner = (rawWinner === 'player1' || rawWinner === 'J1') ? 'J1'
                    : ((rawWinner === 'player2' || rawWinner === 'J2') ? 'J2'
                    : 'Empate');
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'finished',
                    winner: finalWinner,
                    remainingSeconds: 0,
                    scoreJ1: payload.scoreJ1 ?? payload.ScoreJ1 ?? payload.player1?.score ?? previous.scoreJ1,
                    scoreJ2: payload.scoreJ2 ?? payload.ScoreJ2 ?? payload.player2?.score ?? previous.scoreJ2,
                    hpJ1: payload.hpJ1 ?? payload.HpJ1 ?? payload.player1?.hp ?? incomingData.hpJ1 ?? previous.hpJ1,
                    hpJ2: payload.hpJ2 ?? payload.HpJ2 ?? payload.player2?.hp ?? incomingData.hpJ2 ?? previous.hpJ2,
                    totalTapsJ1: payload.totalTapsJ1 ?? payload.TotalTapsJ1 ?? payload.player1?.totalTaps ?? previous.totalTapsJ1,
                    totalTapsJ2: payload.totalTapsJ2 ?? payload.TotalTapsJ2 ?? payload.player2?.totalTaps ?? previous.totalTapsJ2
                }));

                triggerKnockoutCinematic(finalWinner, activeThemeData);
                break;
            }

            case 'aura_reset':
            case 'fighters_game_reset': {
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'idle',
                    scoreJ1: 0,
                    scoreJ2: 0,
                    hpJ1: 100.0,
                    hpJ2: 100.0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0,
                    comboJ1: 0,
                    comboJ2: 0,
                    transformIndexJ1: 0,
                    transformIndexJ2: 0,
                    winner: null,
                    remainingSeconds: previous.roundDuration || 10
                }));
                setCountdownDisplayNumber(null);
                setEphemeralConfettiParticleList([]);
                hasFinishedCinematicTriggeredRef.current = false;
                if (activeThemeData) {
                    initializeTransformationDecks(activeThemeData);
                }
                break;
            }

            default:
                break;
        }
    }, [
        payload,
        activeThemeData,
        combatEngineState.scoreJ1,
        combatEngineState.scoreJ2,
        combatEngineState.hpJ1,
        combatEngineState.hpJ2,
        triggerComicComboPopup,
        triggerPlayerTransformation,
        triggerKnockoutCinematic,
        initializeTransformationDecks
    ]);

    // =========================================================================
    // 12. TEMPORIZADOR LOCAL AUTORITATIVO (0% CPU EN REPOSO)
    // =========================================================================
    useEffect(() => {
        if (combatEngineState.state !== 'playing' || !combatEngineState.endsAt) {
            if (localCountdownIntervalTimerRef.current) {
                clearInterval(localCountdownIntervalTimerRef.current);
                localCountdownIntervalTimerRef.current = null;
            }
            return;
        }

        localCountdownIntervalTimerRef.current = setInterval(() => {
            const currentTimestamp = Date.now();
            const differenceMs = combatEngineState.endsAt - currentTimestamp;
            const updatedSeconds = Math.max(0, Math.round((differenceMs / 1000) * 10) / 10);

            setCombatEngineState((previousState) => {
                if (previousState.state !== 'playing') return previousState;
                return { ...previousState, remainingSeconds: updatedSeconds };
            });

            if (differenceMs <= 0) {
                clearInterval(localCountdownIntervalTimerRef.current);
                localCountdownIntervalTimerRef.current = null;
            }
        }, 100);

        return () => {
            if (localCountdownIntervalTimerRef.current) {
                clearInterval(localCountdownIntervalTimerRef.current);
                localCountdownIntervalTimerRef.current = null;
            }
        };
    }, [combatEngineState.state, combatEngineState.endsAt]);

    // Cleanup effects on unmount
    useEffect(() => {
        return () => {
            if (localCountdownIntervalTimerRef.current) clearInterval(localCountdownIntervalTimerRef.current);
            if (screenShakeResetTimerRef.current) clearTimeout(screenShakeResetTimerRef.current);
            if (confettiAutoDestroyTimerRef.current) clearTimeout(confettiAutoDestroyTimerRef.current);
        };
    }, []);

    // =========================================================================
    // 13. CÁLCULO DE BARRAS DE VIDA Y AURA (ESTILO FIGHTING GAME)
    // =========================================================================
    const gameBackgroundType = activeThemeData?.gameBackgroundType || DEFAULT_GAME_CONFIGURATION.gameBackgroundType;
    const gameBackgroundColor = activeThemeData?.gameBackgroundColor || DEFAULT_GAME_CONFIGURATION.gameBackgroundColor;
    const gameBackgroundUrl = activeThemeData?.gameBackgroundUrl ? resolveMediaUrl(activeThemeData.gameBackgroundUrl) : '';
    const gameTextColor = activeThemeData?.gameTextColor || DEFAULT_GAME_CONFIGURATION.gameTextColor;
    const player1Color = activeThemeData?.player1Color || DEFAULT_GAME_CONFIGURATION.player1Color;
    const player2Color = activeThemeData?.player2Color || DEFAULT_GAME_CONFIGURATION.player2Color;

    const isHealthMode = combatEngineState.gameMode === 'health_ko' ||
        combatEngineState.gameMode === 'health_timed' ||
        combatEngineState.gameMode === 'HealthKO' ||
        combatEngineState.gameMode === 'HealthTimed';

    // Calcular ancho porcentual de las barras de aura neón / vida
    let playerOneBarPercentage = 100;
    let playerTwoBarPercentage = 100;

    if (combatEngineState.state === 'finished') {
        if (combatEngineState.winner === 'J1' || combatEngineState.winner === 'player1') {
            playerOneBarPercentage = isHealthMode ? Math.max(15, Math.round(combatEngineState.hpJ1 ?? 100)) : 100;
            playerTwoBarPercentage = 0; // K.O.
        } else if (combatEngineState.winner === 'J2' || combatEngineState.winner === 'player2') {
            playerOneBarPercentage = 0; // K.O.
            playerTwoBarPercentage = isHealthMode ? Math.max(15, Math.round(combatEngineState.hpJ2 ?? 100)) : 100;
        } else {
            playerOneBarPercentage = isHealthMode ? Math.round(combatEngineState.hpJ1 ?? 100) : 100;
            playerTwoBarPercentage = isHealthMode ? Math.round(combatEngineState.hpJ2 ?? 100) : 100;
        }
    } else if (combatEngineState.state === 'playing') {
        if (isHealthMode) {
            playerOneBarPercentage = Math.max(0, Math.min(100, Math.round(combatEngineState.hpJ1 ?? 100)));
            playerTwoBarPercentage = Math.max(0, Math.min(100, Math.round(combatEngineState.hpJ2 ?? 100)));
        } else {
            const totalCombinedScore = combatEngineState.scoreJ1 + combatEngineState.scoreJ2;
            if (totalCombinedScore > 0) {
                playerOneBarPercentage = Math.min(100, Math.max(15, Math.round((combatEngineState.scoreJ1 / totalCombinedScore) * 100)));
                playerTwoBarPercentage = Math.min(100, Math.max(15, Math.round((combatEngineState.scoreJ2 / totalCombinedScore) * 100)));
            } else {
                playerOneBarPercentage = 100;
                playerTwoBarPercentage = 100;
            }
        }
    }

    // Modo Individual (Por Turnos) - Detección de bando en espera
    const isTurnsMode = combatEngineState.gameMode === 'turns';
    const isPlayerOneDimmed = isTurnsMode && combatEngineState.activeFansJ2 > 0 && combatEngineState.activeFansJ1 === 0;
    const isPlayerTwoDimmed = isTurnsMode && combatEngineState.activeFansJ1 > 0 && combatEngineState.activeFansJ2 === 0;

    // =========================================================================
    // 14. RENDERIZADO DEL FONDO DINÁMICO
    // =========================================================================
    const renderCombatBackgroundLayer = () => {
        if (gameBackgroundType === 'video' && gameBackgroundUrl) {
            return (
                <video
                    src={gameBackgroundUrl}
                    className="aura-screen-game__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (gameBackgroundType === 'image' && gameBackgroundUrl) {
            return (
                <div
                    className="aura-screen-game__background-image"
                    style={{ backgroundImage: `url(${gameBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="aura-screen-game__background-color"
                style={{ backgroundColor: gameBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 15. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <div
            className={`aura-screen-game ${screenShakeClassName}`}
            role="region"
            aria-label="Escenario de Combate Farmeando Aura"
        >
            {/* Fondo Dinámico con GPU Compositing */}
            {renderCombatBackgroundLayer()}

            {/* Flash Rojo de Daño Cinemático K.O. */}
            {isCinematicDamageFlashActive && (
                <div className="aura-screen-game__damage-flash-overlay" />
            )}

            {/* Contenedor de Confeti Efímero (Auto-destrucción a los 3s) */}
            {ephemeralConfettiParticleList.length > 0 && (
                <div className="aura-screen-game__confetti-container">
                    {ephemeralConfettiParticleList.map((confettiParticle) => (
                        <div
                            key={confettiParticle.id}
                            className="aura-screen-game__confetti-particle ccgame__confetti-piece"
                            style={{
                                backgroundColor: confettiParticle.color,
                                left: `${confettiParticle.leftPercentage}%`,
                                width: `${confettiParticle.sizePixels}px`,
                                height: `${confettiParticle.sizePixels * 1.4}px`,
                                borderRadius: confettiParticle.borderRadius,
                                animationDuration: `${confettiParticle.durationSeconds}s`,
                                animationDelay: `${confettiParticle.delaySeconds}s`
                            }}
                        />
                    ))}
                </div>
            )}

            {/* ============================================================= */}
            {/* HUD SUPERIOR: BARRAS NEÓN INCLINADAS Y TEMPORIZADOR CENTRAL    */}
            {/* ============================================================= */}
            <div style={{ zIndex: '1', textAlign: 'center', marginTop: '0.5em' }}>
                <img
                    src="/img/page/xolos-five.png"
                    alt="Logo Xolos"
                    style={{ width: '350px' }}
                />
            </div>

            <header className="aura-screen-game__hud-header">
                {/* LADO IZQUIERDO: BARRAS Y MARCADORES JUGADOR 1 */}
                <div className="aura-screen-game__hud-fighter aura-screen-game__hud-fighter--j1">
                    <div className="aura-screen-game__fighter-meta">
                        <span
                            className="aura-screen-game__fighter-badge"
                            style={{ backgroundColor: player1Color, color: '#000000' }}
                        >
                            JUGADOR 1
                        </span>
                        {/* <span className="aura-screen-game__fans-counter" style={{ color: gameTextColor }}>
                            👥 {combatEngineState.activeFansJ1} FANS
                        </span> */}
                    </div>

                    {/* Barra Neón Inclinada (Fighting Game Style) */}
                    <div className="aura-screen-game__health-bar-wrapper aura-screen-game__health-bar-wrapper--j1">
                        <div
                            className="aura-screen-game__health-bar-fill"
                            style={{
                                width: `${playerOneBarPercentage}%`,
                                background: `linear-gradient(90deg, #ffffff, ${player1Color})`,
                                boxShadow: `0 0 25px ${player1Color}`
                            }}
                        />
                    </div>

                    {/* Marcador Gigante de Aura / Salud con Bangers Font */}
                    <div className="aura-screen-game__aura-score-display" style={{ color: gameTextColor }}>
                        <span className="aura-screen-game__score-value" style={{ color: player1Color }}>
                            {isHealthMode ? `${Math.round(combatEngineState.hpJ1 ?? 100)}%` : Math.round(combatEngineState.scoreJ1)}
                        </span>
                        <span className="aura-screen-game__score-label">{isHealthMode ? 'SALUD (HP)' : 'PTS AURA'}</span>
                        {/* <span className="aura-screen-game__taps-sublabel">⚡ {combatEngineState.totalTapsJ1} Taps</span> */}
                    </div>
                </div>

                {/* CENTRO: TEMPORIZADOR RESPIRATORIO Y ESTADO */}
                <div className="aura-screen-game__hud-center">
                    <div
                        className={`aura-screen-game__timer-badge ${combatEngineState.remainingSeconds <= 3 && combatEngineState.state === 'playing' ? 'aura-screen-game__timer-badge--danger' : ''}`}
                    >
                        <span className="aura-screen-game__timer-digits">
                            {combatEngineState.state === 'finished'
                                ? 'K.O.'
                                : combatEngineState.remainingSeconds.toFixed(1)}
                        </span>
                        <span className="aura-screen-game__timer-label">SEG</span>
                    </div>

                    {combatEngineState.state === 'paused' && (
                        <div className="aura-screen-game__paused-badge">PAUSADO</div>
                    )}
                </div>

                {/* LADO DERECHO: BARRAS Y MARCADORES JUGADOR 2 */}
                <div className="aura-screen-game__hud-fighter aura-screen-game__hud-fighter--j2">
                    <div className="aura-screen-game__fighter-meta aura-screen-game__fighter-meta--j2">
                        {/* <span className="aura-screen-game__fans-counter" style={{ color: gameTextColor }}>
                            👥 {combatEngineState.activeFansJ2} FANS
                        </span> */}
                        <span
                            className="aura-screen-game__fighter-badge"
                            style={{ backgroundColor: player2Color, color: '#000000' }}
                        >
                            JUGADOR 2
                        </span>
                    </div>

                    {/* Barra Neón Inclinada (Fighting Game Style) */}
                    <div className="aura-screen-game__health-bar-wrapper aura-screen-game__health-bar-wrapper--j2">
                        <div
                            className="aura-screen-game__health-bar-fill"
                            style={{
                                width: `${playerTwoBarPercentage}%`,
                                background: `linear-gradient(270deg, #ffffff, ${player2Color})`,
                                boxShadow: `0 0 25px ${player2Color}`
                            }}
                        />
                    </div>

                    {/* Marcador Gigante de Aura / Salud con Bangers Font */}
                    <div className="aura-screen-game__aura-score-display aura-screen-game__aura-score-display--j2" style={{ color: gameTextColor }}>
                        <span className="aura-screen-game__score-value" style={{ color: player2Color }}>
                            {isHealthMode ? `${Math.round(combatEngineState.hpJ2 ?? 100)}%` : Math.round(combatEngineState.scoreJ2)}
                        </span>
                        <span className="aura-screen-game__score-label">{isHealthMode ? 'SALUD (HP)' : 'PTS AURA'}</span>
                        {/* <span className="aura-screen-game__taps-sublabel">⚡ {combatEngineState.totalTapsJ2} Taps</span> */}
                    </div>
                </div>
            </header>

            {/* ============================================================= */}
            {/* ZONA CENTRAL DE COMBATE: LUCHADORES Y ANIMACIONES              */}
            {/* ============================================================= */}
            <main className="aura-screen-game__arena-stage">
                {/* LUCHADOR 1 (LOCAL) */}
                <div
                    className={`aura-screen-game__fighter-spot aura-screen-game__fighter-spot--j1 ${isPlayerOneDimmed ? 'aura-screen-game__fighter-spot--dimmed' : ''}`}
                >
                    <div
                        className="aura-screen-game__aura-energy-halo"
                        style={{ borderColor: player1Color, boxShadow: `0 0 50px ${player1Color}` }}
                    />
                    {(playerOneCurrentSpriteUrl || DEFAULT_GAME_CONFIGURATION.player1Avatar) ? (
                        <img
                            src={playerOneCurrentSpriteUrl || DEFAULT_GAME_CONFIGURATION.player1Avatar}
                            alt="Luchador 1"
                            className={`aura-screen-game__fighter-avatar ${playerOneIsSlidingTransformation ? 'aura-screen-game__fighter-avatar--transforming' : ''} ${combatEngineState.state === 'finished' && (combatEngineState.winner === 'J2' || combatEngineState.winner === 'player2') ? 'aura-screen-game__fighter-avatar--ko' : ''}`}
                            onError={(imageErrorEvent) => {
                                imageErrorEvent.target.style.display = 'none';
                            }}
                        />
                    ) : null}
                </div>

                {/* VS CENTRAL O CUENTA REGRESIVA */}
                <div className="aura-screen-game__versus-spot">
                    {countdownDisplayNumber !== null ? (
                        <div className="aura-screen-game__countdown-overlay">
                            <span className="aura-screen-game__countdown-digit">
                                {countdownDisplayNumber > 0 ? countdownDisplayNumber : '¡DALE!'}
                            </span>
                        </div>
                    ) : (
                        <div className="aura-screen-game__vs-badge" style={{ fontSize: '14em' }}>
                            VS
                        </div>
                    )}
                </div>

                {/* LUCHADOR 2 (VISITANTE) */}
                <div
                    className={`aura-screen-game__fighter-spot aura-screen-game__fighter-spot--j2 ${isPlayerTwoDimmed ? 'aura-screen-game__fighter-spot--dimmed' : ''}`}
                >
                    <div
                        className="aura-screen-game__aura-energy-halo"
                        style={{ borderColor: player2Color, boxShadow: `0 0 50px ${player2Color}` }}
                    />
                    {(playerTwoCurrentSpriteUrl || DEFAULT_GAME_CONFIGURATION.player2Avatar) ? (
                        <img
                            src={playerTwoCurrentSpriteUrl || DEFAULT_GAME_CONFIGURATION.player2Avatar}
                            alt="Luchador 2"
                            className={`aura-screen-game__fighter-avatar ${playerTwoIsSlidingTransformation ? 'aura-screen-game__fighter-avatar--transforming' : ''} ${combatEngineState.state === 'finished' && (combatEngineState.winner === 'J1' || combatEngineState.winner === 'player1') ? 'aura-screen-game__fighter-avatar--ko' : ''}`}
                            onError={(imageErrorEvent) => {
                                imageErrorEvent.target.style.display = 'none';
                            }}
                        />
                    ) : null}
                </div>

                {/* ========================================================= */}
                {/* POPUPS DE COMBO FLOTANTES ESTILO CÓMIC BANGERS            */}
                {/* ========================================================= */}
                {activeFloatingCombosList.map((comboItem) => (
                    <div
                        key={comboItem.id}
                        className={`aura-screen-game__combo-popup aura-screen-game__combo-popup--${comboItem.side.toLowerCase()}`}
                        style={{
                            fontSize: comboItem.fontSize,
                            color: comboItem.side === 'J1' ? player1Color : player2Color
                        }}
                    >
                        {comboItem.text}
                    </div>
                ))}

                {/* ========================================================= */}
                {/* BANNER DE RESULTADO Y PODIUM FINAL                        */}
                {/* ========================================================= */}
                {combatEngineState.state === 'finished' && (
                    <div className="aura-screen-game__podium-banner">
                        <div className="aura-screen-game__podium-banner__card">
                            <img
                                src="/img/page/xolos-five.png"
                                alt="Logo Xolos"
                                style={{ width: '450px' }}
                            />
                            <span className="aura-screen-game__podium-banner__subtitle">
                                ¡FIN DE LA BATALLA!
                            </span>
                            <h2 className="aura-screen-game__podium-banner__title">
                                {combatEngineState.winner === 'J1' || combatEngineState.winner === 'player1'
                                    ? '¡GANADOR JUGADOR 1! 🏆'
                                    : combatEngineState.winner === 'J2' || combatEngineState.winner === 'player2'
                                        ? '¡GANADOR JUGADOR 2! 🏆'
                                        : '¡EMPATE ÉPICO! ⚡'}
                            </h2>
                            {/* <p className="aura-screen-game__podium-banner__score-summary" style={{ fontSize: '2.5rem', fontWeight: 900, marginTop: '10px', color: '#ffcc00' }}>
                                J1: {Math.round(combatEngineState.scoreJ1)} pts ({combatEngineState.totalTapsJ1} taps) — VS — J2: {Math.round(combatEngineState.scoreJ2)} pts ({combatEngineState.totalTapsJ2} taps)
                            </p> */}
                        </div>
                    </div>
                )}
            </main>

            <footer className="aura-screen-cover__footer-credits" style={{ fontSize: '26px' }}>
                Hecho con ♥ por Interaktia
            </footer>
        </div>
    );
};

FightersGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        count: PropTypes.number,
        startedAt: PropTypes.number,
        endsAt: PropTypes.number,
        durationSeconds: PropTypes.number,
        gameMode: PropTypes.string,
        winner: PropTypes.string,
        scoreJ1: PropTypes.number,
        scoreJ2: PropTypes.number,
        totalTapsJ1: PropTypes.number,
        totalTapsJ2: PropTypes.number,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object
    })
};

FightersGame.defaultProps = {
    payload: null
};

export const FarmeandoAuraGame = FightersGame;
export default FightersGame;
